import { Location } from "history";

import { LoginApi } from "lib/api/security/login";
import { LoginAttempt, SuccessLoginResult, User } from "lib/data/model";
import { Event, EventDispatcher } from "lib/EventDispatcher";
import { SessionService } from "./SessionService";


export class LoginService {

	public static setPreviousLocation = (location: Location) => {

		sessionStorage.setItem("LoginService.originalRoute", location.pathname);
	}

	public static getPreviousLocation = (): string | undefined => {

		// Get Route From Session Storage
		const originalLocation = sessionStorage.getItem("LoginService.originalRoute");

		// Remove Route From Session Storage
		sessionStorage.removeItem("LoginService.originalRoute");

		// Check Route
		if (originalLocation !== null) {

			return originalLocation;
		}
		else {

			// Return Nothing
			return undefined;
		}
	}

	public static async login(username: string, password: string): Promise<LoginAttempt | undefined> {

		// Log User In
		const loginResult = await LoginApi.login(username, password);

		// Check Login Result
		if (loginResult !== undefined) {

			// Switch On Login Result Type
			switch (loginResult.$type) {

				case SuccessLoginResult.$type:

					// Cast Result
					const successResult = loginResult as SuccessLoginResult;

					// Set Session In Storage
					SessionService.instance.setSession(successResult.session);

					// Clear Await Login Flag
					sessionStorage.removeItem("LoginService.mustAwaitLogin");

					// Register For Session Refresh
					await SessionService.instance.registerSignalR();

					// Set Session Timer
					SessionService.instance.setTimer(successResult.session);

					// Trigger Logged In Event
					EventDispatcher.dispatch(Event.LoggedIn);

					break;

				default:

					// Force Logout
					await LoginService.logout(true);
			}
		}

		// Return Attempt
		return loginResult;
	}

	public static async logout(isQuiet: boolean = false) {

		// Log User Out
		await LoginApi.logout();

		// Clear Stored Session
		SessionService.instance.clearSession();

		// Unregister SignalR
		await SessionService.instance.unregisterSignalR();

		// Clear Session Timers
		SessionService.instance.clearTimer();

		// Check For Quiet Logout
		if (!isQuiet) {

			// Remove Route From Session Storage
			sessionStorage.removeItem("LoginService.originalRoute");
		}

		// Trigger Logged Out Event
		EventDispatcher.dispatch(Event.LoggedOut, isQuiet);

		// Remove Permission
		sessionStorage.removeItem("User.Permissions");
	}

	public static async getUserIfExists(): Promise<User | undefined> {

		// Get Login Status
		const isLoggedIn = await LoginService.isLoggedIn();

		// Check If User Is Logged In
		if (isLoggedIn) {

			// Get Session From Storage
			const session = SessionService.instance.getSession();

			// Check Session
			if (session !== undefined) {

				// Return Session User
				return session.user;
			}
		}

		// Return Nothing
		return undefined;
	}

	public static async isLoggedIn(): Promise<boolean> {

		// Retrieve Whether Login Checks Need To Wait For Login
		const shouldAwaitLogin = sessionStorage.getItem("LoginService.mustAwaitLogin");

		// Check For Need To Await Login
		if (shouldAwaitLogin === null) {

			// Get Session From Storage
			const session = SessionService.instance.getSession();

			// Check Session
			if (session !== undefined) {

				// Check For Expired Session
				if (session.expiresAt.getTime() > new Date().getTime()) {

					// Return Is Logged In
					return true;
				}
				else {

					// Clear Stored Session
					SessionService.instance.clearSession();

					// Instruct Login Service To Wait For Login
					sessionStorage.setItem("LoginService.mustAwaitLogin", "");

					// Trigger (Forced) Logged Out Event
					EventDispatcher.dispatch(Event.LoggedOut, true);
				}
			}
			else {

				// Retrieve Whether Login Checks Need To Wait For Login
				let isSessionCheckRunning = sessionStorage.getItem("LoginService.isSessionCheckRunning") !== null;

				// Check For Need To Await Login
				if (!isSessionCheckRunning) {

					// Indicate That Session Check Is Running
					sessionStorage.setItem("LoginService.isSessionCheckRunning", "");

					// Check With API For Active Session
					const activeSession = await LoginApi.getSession();

					// Check Active Session
					if (activeSession !== undefined) {

						// Store Session In Storage
						SessionService.instance.setSession(activeSession);

						// Indicate Session Check Is Not Running
						sessionStorage.removeItem("LoginService.isSessionCheckRunning");

						// Return Logged In
						return true;
					}
					else {

						// Instruct Login Service To Wait For Login
						sessionStorage.setItem("LoginService.mustAwaitLogin", "");

						// Indicate Session Check Is Not Running
						sessionStorage.removeItem("LoginService.isSessionCheckRunning");
					}
				}
				else {

					// Loop Until Check Is Finished
					do {

						await new Promise((resolve) => setTimeout(() => resolve, 250));

						isSessionCheckRunning = sessionStorage.getItem("LoginService.isSessionCheckRunning") !== null;
					}
					while (isSessionCheckRunning);

					// Check Outcome
					if (sessionStorage.getItem("LoginService.mustAwaitLogin") !== null) {

						// Return Logged In
						return true;
					}
				}
			}
		}

		// Return Not Logged In
		return false;
	}
}
